import { Navigate, useLocation, Outlet } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { Loader } from '../UI/LoadingSpinner'

// Компонент для защиты маршрутов
const ProtectedRoute = ({ 
  children, 
  requiredRole = null, 
  requiredPermissions = [], 
  fallback = '/login' 
}) => {
  const { user, isAuthenticated, loading, hasPermission } = useAuth()
  const location = useLocation()

  // Показываем спиннер во время проверки аутентификации
  if (loading) {
    return (
      <Loader variant="component" />
    )
  }

  // Если пользователь не аутентифицирован, перенаправляем на страницу входа
  if (!isAuthenticated) {
    return <Navigate to={fallback} state={{ from: location }} replace />
  }

  // Проверяем роль, если она требуется
  if (requiredRole && user?.role !== requiredRole) {
    return <Navigate to="/unauthorized" replace />
  }

  // Проверяем разрешения, если они требуются
  if (requiredPermissions.length > 0) {
    const hasAllPermissions = requiredPermissions.every(([service, resource, action]) => 
      hasPermission(service, resource, action)
    )
    
    if (!hasAllPermissions) {
      return <Navigate to="/unauthorized" replace />
    }
  }

  // Если все проверки пройдены, отображаем защищенный контент или Outlet для вложенных маршрутов
  return children || <Outlet />
}

// Компонент для маршрутов, доступных только неаутентифицированным пользователям
export const PublicOnlyRoute = ({ children, fallback = '/' }) => {
  const { isAuthenticated, loading } = useAuth()

  if (loading) {
    return (
      <Loader variant="component" size="md" />
    )
  }

  if (isAuthenticated) {
    return <Navigate to={fallback} replace />
  }

  return children
}

// Компонент для маршрутов с условным доступом
export const ConditionalRoute = ({ 
  children, 
  condition, 
  fallback = '/unauthorized',
  loadingFallback = null 
}) => {
  const { loading } = useAuth()

  if (loading && loadingFallback) {
    return loadingFallback
  }

  if (loading) {
    return (
      <Loader variant="component" size="md" />
    )
  }

  if (!condition) {
    return <Navigate to={fallback} replace />
  }

  return children
}

// Компонент для маршрутов с множественными ролями
export const MultiRoleRoute = ({ 
  children, 
  allowedRoles = [], 
  fallback = '/unauthorized' 
}) => {
  const { user, isAuthenticated, loading } = useAuth()

  if (loading) {
    return (
      <Loader variant="component" size="md" />
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
    return <Navigate to={fallback} replace />
  }

  return children
}

// Компонент для маршрутов с проверкой владения ресурсом
export const OwnershipRoute = ({ 
  children, 
  resourceOwnerId, 
  fallback = '/unauthorized',
  adminOverride = true 
}) => {
  const { user, isAuthenticated, loading, hasPermission } = useAuth()

  if (loading) {
    return (
      <Loader variant="component" size="md" />
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  // Администраторы могут получить доступ к любым ресурсам (если есть admin permission)
  if (adminOverride && hasPermission('talent-service', '*', '*')) {
    return children
  }

  // Проверяем владение ресурсом
  if (user?.id !== resourceOwnerId) {
    return <Navigate to={fallback} replace />
  }

  return children
}

export default ProtectedRoute